// frontend/components/HrEmployeeTimeTab.jsx
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyTimeEntries,
  checkInTimeEntry,
  checkOutTimeEntry,
} from "../features/hr/hrThunks.js";
import { Play, Square, Clock, CalendarDays, ListChecks } from "lucide-react";
import "../styles/hr/hremployeetime.css";

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

const parseLocalDateFromYMD = (ymd) => {
  if (!ymd) return null;
  const parts = String(ymd).split("-");
  if (parts.length !== 3) return null;

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const formatTime = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

const formatDateLabel = (ymd) => {
  const date = parseLocalDateFromYMD(ymd);
  if (!date) return "—";
  return date.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDurationHours = (entry) => {
  if (!entry) return null;

  if (entry.duration_hours != null) {
    const parsed = Number(entry.duration_hours);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (!entry.check_in || !entry.check_out) return null;

  const start = new Date(entry.check_in);
  const end = new Date(entry.check_out);

  const diffMs = end - start;
  if (!Number.isFinite(diffMs) || diffMs <= 0) return null;

  const hours = diffMs / (1000 * 60 * 60);
  return Number(hours.toFixed(2));
};

const sumHours = (entries) =>
  entries.reduce((total, entry) => total + (getDurationHours(entry) || 0), 0);

const HrEmployeeTimeTab = () => {
  const dispatch = useDispatch();

  const myTimeEntries = useSelector((state) => state.hr.myTimeEntries) || [];

  const loadingMyTimeEntries = useSelector((state) => state.hr.loadingMyTimeEntries);
  const errorMyTimeEntries = useSelector((state) => state.hr.errorMyTimeEntries);

  const loadingCheckIn = useSelector((state) => state.hr.loadingCheckIn);
  const errorCheckIn = useSelector((state) => state.hr.errorCheckIn);

  const loadingCheckOut = useSelector((state) => state.hr.loadingCheckOut);
  const errorCheckOut = useSelector((state) => state.hr.errorCheckOut);

  useEffect(() => {
    dispatch(fetchMyTimeEntries());
  }, [dispatch]);

  const sortedEntries = useMemo(() => {
    const copy = Array.isArray(myTimeEntries) ? myTimeEntries.slice() : [];
    copy.sort((a, b) => {
      const dateA = String(a?.date || "");
      const dateB = String(b?.date || "");
      if (dateA !== dateB) return dateB.localeCompare(dateA);

      const checkInA = String(a?.check_in || "");
      const checkInB = String(b?.check_in || "");
      return checkInB.localeCompare(checkInA);
    });
    return copy;
  }, [myTimeEntries]);

  const openEntry = useMemo(
    () => sortedEntries.find((entry) => entry && entry.check_out == null),
    [sortedEntries]
  );

  const hasOpenEntry = Boolean(openEntry);

  const { todayHours, weekHours, lastClosedEntry } = useMemo(() => {
    const now = new Date();
    const todayKey = formatDateKey(now);

    const dayOfWeek = now.getDay(); // 0 domingo, 1 lunes...
    const diffToMonday = (dayOfWeek + 6) % 7; // lunes = 0
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(now.getDate() - diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const mondayKey = formatDateKey(monday);
    const sundayKey = formatDateKey(sunday);

    const todayEntries = sortedEntries.filter((entry) => entry?.date === todayKey);
    const weekEntries = sortedEntries.filter(
      (entry) => entry?.date >= mondayKey && entry?.date <= sundayKey
    );

    const lastClosed = sortedEntries.find((entry) => entry?.check_out);

    return {
      todayHours: sumHours(todayEntries),
      weekHours: sumHours(weekEntries),
      lastClosedEntry: lastClosed || null,
    };
  }, [sortedEntries]);

  const safeTodayHours = Number(todayHours || 0);
  const safeWeekHours = Number(weekHours || 0);

  const safeLastClosedHours =
    lastClosedEntry != null ? Number(getDurationHours(lastClosedEntry) || 0) : 0;

  const handleCheckIn = () => {
    if (loadingCheckIn || loadingCheckOut) return;
    dispatch(checkInTimeEntry());
  };

  const handleCheckOut = () => {
    if (loadingCheckIn || loadingCheckOut) return;
    dispatch(checkOutTimeEntry());
  };

  const now = new Date();
  const todayLabel = now.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });

  const activeSinceText =
    hasOpenEntry && openEntry?.check_in ? formatTime(openEntry.check_in) : null;

  const checkInErrorText = getErrorText(errorCheckIn);
  const checkOutErrorText = getErrorText(errorCheckOut);
  const timeEntriesErrorText = getErrorText(errorMyTimeEntries);

  return (
    <div className="hr-time-tab">
      <div className="hr-time-header-card">
        <div className="hr-time-header-left">
          <p className="hr-time-header-date">Hoy, {todayLabel}</p>
          <h2 className="hr-time-header-title">Control de jornada</h2>

          <p className="hr-time-header-subtitle">
            {hasOpenEntry
              ? activeSinceText
                ? `Estás fichado desde las ${activeSinceText}.`
                : "Tienes una jornada abierta."
              : "Ahora mismo no tienes ninguna jornada abierta."}
          </p>

          {hasOpenEntry ? (
            <span className="hr-pill hr-pill--status">Jornada en curso</span>
          ) : null}
        </div>

        <div className="hr-time-header-right">
          {hasOpenEntry ? (
            <button
              type="button"
              className="hr-button hr-button--danger"
              onClick={handleCheckOut}
              disabled={loadingCheckOut || loadingCheckIn}
            >
              <Square className="hr-button-icon" aria-hidden="true" />
              {loadingCheckOut ? "Fichando salida..." : "Fichar salida"}
            </button>
          ) : (
            <button
              type="button"
              className="hr-button hr-button--primary"
              onClick={handleCheckIn}
              disabled={loadingCheckIn || loadingCheckOut}
            >
              <Play className="hr-button-icon" aria-hidden="true" />
              {loadingCheckIn ? "Fichando entrada..." : "Fichar entrada"}
            </button>
          )}

          <p className="hr-time-header-helper">
            Recuerda fichar al inicio y al final de la jornada.
          </p>
        </div>
      </div>

      {(checkInErrorText || checkOutErrorText) ? (
        <div className="hr-error-banner">
          {checkInErrorText ? (
            <span>Error al fichar entrada: {checkInErrorText}</span>
          ) : null}
          {checkOutErrorText ? (
            <span>Error al fichar salida: {checkOutErrorText}</span>
          ) : null}
        </div>
      ) : null}

      <div className="hr-time-grid">
        <div className="hr-time-metrics">
          <div className="hr-metric-card hr-metric-card--primary">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Horas de hoy</span>
              <Clock className="hr-metric-icon" aria-hidden="true" />
            </div>

            <div className="hr-metric-value">
              {loadingMyTimeEntries ? "…" : safeTodayHours.toFixed(2)}
              <span className="hr-metric-unit">h</span>
            </div>

            <p className="hr-metric-helper">Basado en tus fichajes de hoy.</p>
          </div>

          <div className="hr-metric-card">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Esta semana</span>
              <CalendarDays className="hr-metric-icon" aria-hidden="true" />
            </div>

            <div className="hr-metric-value">
              {loadingMyTimeEntries ? "…" : safeWeekHours.toFixed(2)}
              <span className="hr-metric-unit">h</span>
            </div>

            <p className="hr-metric-helper">
              De lunes a domingo de la semana actual.
            </p>
          </div>

          <div className="hr-metric-card">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Último fichaje cerrado</span>
              <ListChecks className="hr-metric-icon" aria-hidden="true" />
            </div>

            <div className="hr-metric-value">
              {lastClosedEntry ? safeLastClosedHours.toFixed(2) : "—"}
              <span className="hr-metric-unit">h</span>
            </div>

            <p className="hr-metric-helper">
              {lastClosedEntry
                ? `El ${formatDateLabel(lastClosedEntry.date)}`
                : "Todavía no hay fichajes cerrados."}
            </p>
          </div>
        </div>

        <div className="hr-time-history-card">
          <h3 className="hr-info-title">
            <ListChecks className="hr-info-icon" aria-hidden="true" />
            Historial reciente
          </h3>

          {loadingMyTimeEntries ? (
            <div className="hr-time-history-empty">Cargando fichajes...</div>
          ) : timeEntriesErrorText ? (
            <div className="hr-error-text">Error: {timeEntriesErrorText}</div>
          ) : sortedEntries.length === 0 ? (
            <div className="hr-time-history-empty">
              Aún no tienes fichajes registrados.
            </div>
          ) : (
            <div className="hr-time-history-table-wrapper">
              <table className="hr-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Entrada</th>
                    <th>Salida</th>
                    <th>Horas</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.slice(0, 10).map((entry) => {
                    const isOpen = entry?.check_out == null;
                    const duration = getDurationHours(entry);

                    return (
                      <tr key={entry.id} className={isOpen ? "is-open-row" : ""}>
                        <td>{formatDateLabel(entry.date)}</td>
                        <td>{formatTime(entry.check_in)}</td>
                        <td>{isOpen ? "Abierto" : formatTime(entry.check_out)}</td>
                        <td>{duration != null ? duration.toFixed(2) : "—"}</td>
                        <td>
                          <span
                            className={
                              isOpen
                                ? "hr-badge hr-badge--open"
                                : "hr-badge hr-badge--closed"
                            }
                          >
                            {isOpen ? "En curso" : "Cerrado"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HrEmployeeTimeTab;